import { useMemo, useEffect, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { createAppTheme, resolveThemeColors } from '@/theme';
import { dataGridLocale } from '@/lib/dataGridLocale';
import { cachedThemeColors, cacheThemeColors } from '@/lib/themeCache';
import { useUserPreferences, type ThemeMode } from '@/contexts/UserPreferencesContext';
import type { PaletteMode } from '@mui/material/styles';

const THEME_CACHE_KEY = 'nexo.theme.mode';

function useSystemColorScheme(): PaletteMode {
  const [mode, setMode] = useState<PaletteMode>(() =>
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setMode(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return mode;
}

function resolveMode(preference: ThemeMode, system: PaletteMode): PaletteMode {
  if (preference === 'light' || preference === 'dark') return preference;
  return system;
}

export function ThemePreferenceProvider({ children }: { children: ReactNode }) {
  const {
    theme: themePreference, accentColor, customTheme, isLoading: preferencesLoading, customThemeLoading,
  } = useUserPreferences();
  // The colours wait for both the user's choice and the tenant's colours that choice may name.
  const isLoading = preferencesLoading || customThemeLoading;
  const systemMode = useSystemColorScheme();

  // Use localStorage cache while API is loading to avoid color flash
  const cachedMode = localStorage.getItem(THEME_CACHE_KEY) as ThemeMode | null;

  const effectiveMode = isLoading && cachedMode ? cachedMode : themePreference;

  const resolvedMode = resolveMode(effectiveMode, systemMode);

  // Persist to localStorage when API values arrive
  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem(THEME_CACHE_KEY, themePreference);
      cacheThemeColors(accentColor, customTheme);
    }
  }, [isLoading, themePreference, accentColor, customTheme]);

  // The theme also carries the component texts for the user's language, so a language change
  // re-translates every grid.
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const theme = useMemo(() => {
    const colors = isLoading
      ? cachedThemeColors(resolvedMode)
      : resolveThemeColors(resolvedMode, accentColor, customTheme);
    return createAppTheme(resolvedMode, colors, dataGridLocale(language, t));
  }, [isLoading, resolvedMode, accentColor, customTheme, language, t]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
